import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { bool, number, oneOfType, shape, string } from 'prop-types'
import { clamp, memoize, throttle } from 'lodash'
import ImageMagnify from 'react-image-magnify'

// https://github.com/ePages-de/epages-storage/blob/v0.10.7/src/universal/conf/application.conf#L61
const maxStorageDimension = 2560

// Allowed widths to be used in the "srcset" attribute
// (= lazysizes RIaS plugin default value reduced to the epages-storage maximum dimension)
const srcSetWidths = [180, 360, 540, 720, 900, 1080, 1296, 1512, 1728, 1944, 2160, 2376, maxStorageDimension]

// Used to get the responsive image's "srcset" attribute value.
// Constrains the used widths to be up to "maxWidth" (if any).
const getSrcSet = memoize((src, maxWidth) => {
  const widths = maxWidth ? srcSetWidths.filter((w) => w <= maxWidth) : srcSetWidths

  if (maxWidth && maxWidth < maxStorageDimension && Math.max.apply(null, widths) < maxWidth) {
    widths.push(maxWidth)
  }

  return widths.map((w) => `${src}&width=${w}&height=${maxStorageDimension} ${w}w`).join(',')
})

// Used to get appearance values:
// - image container width (used for setting the responsive image's "sizes" attribute value)
// - enlarged image position (used with `react-image-magnify`)
function useZoomAppearance(priorityContainerSelector) {
  const containerRef = useRef(null)
  const [appearance, setAppearance] = useState({ containerWidth: null, enlargedImagePosition: 'over' })

  useLayoutEffect(() => {
    const priorityElement = priorityContainerSelector && document.querySelector(priorityContainerSelector)

    function updateAppearance() {
      const element = priorityElement || containerRef.current
      const { width, right } = element.getBoundingClientRect()

      setAppearance({
        containerWidth: width,
        // Choose "over" if the space on the right of the element isn't sufficient
        // (less than element width plus enlarged image element offset/page scroll bar width).
        enlargedImagePosition: window.innerWidth - right < width + 50 ? 'over' : 'beside',
      })
    }

    updateAppearance()

    // Note: The wait ms value should be lower than the timeout ms value in `useDeferredValue`.
    const handleWindowResize = throttle(updateAppearance, 200)
    window.addEventListener('resize', handleWindowResize)

    return () => {
      window.removeEventListener('resize', handleWindowResize)
      handleWindowResize.cancel()
    }
  }, [priorityContainerSelector])

  return [appearance, containerRef]
}

// Used to get the current small image client height for setting it as a CSS Custom Property (see its usage).
// Deferred with intermediate "reset" to get the accurate value upon HTML responsive image change
// (e.g. resizing, updates due to "sizes" attribute value change).
function useDeferredValue() {
  const timeoutRef = useRef(null)
  const [value, setValue] = useState(null)

  function deferredUpdate(getNextValue) {
    window.clearTimeout(timeoutRef.current)

    // Reset while updating
    if (value !== null && !timeoutRef.current) setValue(null)

    // Deferred update
    // Note: The timeout ms value should be greater than the `throttle` wait ms value in `useZoomAppearance`.
    timeoutRef.current = window.setTimeout(() => {
      setValue(getNextValue())
      timeoutRef.current = null
    }, 300)
  }

  return [value, deferredUpdate]
}

// Used to activate image zoom functionality after the first "mousemove" event,
// or immediately on touch-only devices.
function useActivated() {
  const [activated, setActivated] = useState(false)

  useEffect(() => {
    function activate() {
      setActivated(true)
      window.removeEventListener('mousemove', activate)
    }

    window.addEventListener('mousemove', activate)

    // Immediately activate on touch-only devices.
    // `any-hover: none` means that "none of the available input mechanism(s)
    // can hover conveniently, or there is no pointing input mechanism".
    if (window.matchMedia('(any-hover: none)').matches) activate()

    return () => window.removeEventListener('mousemove', activate)
  }, [])

  return activated
}

function ZoomableImage({ src, width, height, alt, enlargedImagePortalId, imageContainerSelector }) {
  const [appearance, containerRef] = useZoomAppearance(imageContainerSelector)
  const isActivated = useActivated()
  const smallImageRef = useRef(null)

  // Determine whether the image can be enlarged.
  // (extra "clientHeight" check because it's `0` if the container element doesn't have a set "height".)
  const canBeEnlarged = Boolean(
    containerRef.current &&
      (width > containerRef.current.clientWidth * 1.5 ||
        (containerRef.current.clientHeight && height > containerRef.current.clientHeight * 1.5)),
  )

  // Prevent unwanted image upscaling in the browser.
  const [smallImageMaxWidth, setSmallImageMaxWidth] = useState(null)
  const smallImageStyle = { maxWidth: smallImageMaxWidth }

  // We need a set layout height in order to achieve correct `react-image-magnify` behavior (= lens, large image
  // position) when the small image gets contained in its box. We could use `height: max-content` in CSS, but
  // this doesn't work in all browsers. Therefore, to be able to implement a fallback:
  //
  // - Set the current small image client height as a CSS Custom Property on the element.
  // - If not yet known, set its value to `100%` (e.g. initially and upon resizing).
  // - See epages.base "image-gallery.less" for details.
  const [smallImageHeight, updateSmallImageHeight] = useDeferredValue()

  // Props that are used with the zoomable and non-zoomable small image.
  const smallImageProps = {
    src: `${src}&width=600&height=${maxStorageDimension}`,
    srcSet: getSrcSet(src, width),
    sizes: `${appearance.containerWidth}px`,
    alt: alt || '',
    onLoad: (event) => {
      smallImageRef.current = event.target
      updateSmallImageHeight(() => Math.min(smallImageRef.current.clientHeight, height))
      setSmallImageMaxWidth(width <= containerRef.current.clientWidth ? width : null)
    },
  }

  return (
    <div
      ref={containerRef}
      className="ep-zoomable-image"
      data-enlarged-image-position={canBeEnlarged ? appearance.enlargedImagePosition : null}
      style={{
        '--small-image-height': smallImageHeight ? `${smallImageHeight}px` : '100%',
        pointerEvents: isActivated ? null : 'none',
      }}
    >
      {canBeEnlarged ? (
        <ImageMagnify
          key={appearance.enlargedImagePosition}
          className="image-magnify"
          enlargedImagePosition={appearance.enlargedImagePosition}
          enlargedImagePortalId={appearance.enlargedImagePosition === 'beside' ? enlargedImagePortalId : null}
          enlargedImageContainerClassName="image-magnify-enlarged"
          isEnlargedImagePortalEnabledForTouch={true}
          imageClassName="image-magnify-small"
          imageStyle={smallImageStyle}
          smallImage={{ isFluidWidth: true, ...smallImageProps }}
          largeImage={{ src, width, height }}
          lensComponent={CustomLens}
        />
      ) : (
        appearance.containerWidth && (
          <div className="image-magnify">
            <img className="image-magnify-small" style={smallImageStyle} {...smallImageProps} />
          </div>
        )
      )}
    </div>
  )
}

ZoomableImage.propTypes = {
  src: string.isRequired,
  width: oneOfType([number, string]).isRequired,
  height: oneOfType([number, string]).isRequired,
  alt: string,
  // Optional DOM element ID to portal the enlarged image into if its position is "beside":
  enlargedImagePortalId: string,
  // Optional DOM element CSS selector for appearance measurements:
  imageContainerSelector: string,
}

function CustomLens({ cursorOffset, fadeDurationInMs, isActive, isPositionOutside, position, smallImage }) {
  const isVisible = isActive && !isPositionOutside
  const width = cursorOffset.x * 2
  const height = cursorOffset.y * 2
  const top = position.y - cursorOffset.y
  const left = position.x - cursorOffset.x
  const maxTop = smallImage.height - height
  const maxLeft = smallImage.width - width

  return (
    <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, overflow: 'hidden' }}>
      <div
        style={{
          width,
          height,
          opacity: isVisible ? 1 : 0,
          transition: `opacity ${fadeDurationInMs}ms ease-in`,
          boxShadow: '0 0 0 9999px rgba(0,0,0,.4)',
          position: 'absolute',
          top: 0,
          left: 0,
          willChange: 'transform',
          transform: `translate3d(${clamp(left, 0, maxLeft)}px, ${clamp(top, 0, maxTop)}px, 0)`,
        }}
      />
    </div>
  )
}

CustomLens.propTypes = {
  cursorOffset: shape({
    x: number.isRequired,
    y: number.isRequired,
  }).isRequired,
  fadeDurationInMs: number.isRequired,
  isActive: bool,
  isPositionOutside: bool,
  position: shape({
    x: number.isRequired,
    y: number.isRequired,
  }),
  smallImage: shape({
    height: number.isRequired,
    width: number.isRequired,
  }).isRequired,
}

export default ZoomableImage
